import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import useApprovalsPendingCount from "../hooks/useApprovalsPendingCount";
import api from "../lib/api";
import { clearAllGeometry } from "../lib/popoutGeometry";
import { ChangePasswordModal } from "./ChangePasswordModal";
import NotificationsBell from "./NotificationsBell";
import UserMenu from "./UserMenu";
import { RolePreviewBanner } from "./RolePreviewBanner";
import { BrandLogo } from "./BrandLogo";
import SidebarBackToTop from "./SidebarBackToTop";
import BackToTop from "./BackToTop";
import {
  SIDEBAR_LINKS as SIDEBAR_LINKS_RAW,
  SIDEBAR_STORAGE_KEYS,
  LAYOUT_MODES,
  topLinkKey,
  OPS_PATHS,
  popOutOpsWindows,
} from "../lib/sidebarConfig";
import { useSidebarLabelsAll } from "../contexts/SidebarLabelsContext";
import {
  PanelLeftClose,
  PanelLeftOpen,
  ChevronDown,
  ChevronRight,
  PictureInPicture2,
  Menu,
} from "lucide-react";

const COLLAPSED_KEY = SIDEBAR_STORAGE_KEYS.COLLAPSED;
const OPS_GROUP_KEY = SIDEBAR_STORAGE_KEYS.OPS_GROUP;
const OPS_ORDER_KEY = SIDEBAR_STORAGE_KEYS.OPS_ORDER;
const LINKS_ORDER_KEY = SIDEBAR_STORAGE_KEYS.LINKS_ORDER;
const LAYOUT_MODE_KEY = SIDEBAR_STORAGE_KEYS.LAYOUT_MODE;

export default function Layout({ children }) {
  const { user, permissions, previewRole, effectiveRole, effectivePermissions } = useAuth();
  const { overrides: labelOverrides } = useSidebarLabelsAll();
  // Apply per-agency sidebar/H1 rename overrides. Keys:
  //   • route path (`/clients`) for leaf items
  //   • `section:<group|label>` for section headers
  // Missing keys fall through to the code default in sidebarConfig.js.
  const links = useMemo(() => {
    if (!labelOverrides || Object.keys(labelOverrides).length === 0) return SIDEBAR_LINKS_RAW;
    const rename = (item, kind) => {
      const key = kind === "section" ? `section:${item.group || item.label}` : item.to;
      const v = key && labelOverrides[key];
      const nextLabel = (v && String(v).trim()) || item.label;
      const children = Array.isArray(item.children) ? item.children.map((c) => rename(c, "leaf")) : undefined;
      return children ? { ...item, label: nextLabel, children } : { ...item, label: nextLabel };
    };
    return SIDEBAR_LINKS_RAW.map((l) => rename(l, l.children ? "section" : "leaf"));
  }, [labelOverrides]);
  const { total: pendingApprovalsCount } = useApprovalsPendingCount();
  const badgeCounts = { pending_approvals: pendingApprovalsCount };
  // If admin flagged the user as needing a password change, force the modal
  // to open on every mount until they comply.
  const mustChangePw = !!user?.must_change_password;
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSED_KEY) === "1"; } catch { return false; }
  });
  // Per-group open/close state. Default is now **collapsed** — groups auto-
  // expand only when the current path lives inside them, so a fresh session
  // shows just the top-level headers (7 items instead of 35). Users' explicit
  // toggles are persisted per-group and win over auto-expand.
  const [groupsOpen, setGroupsOpen] = useState(() => {
    const groups = SIDEBAR_LINKS_RAW.filter((l) => l.children);
    const state = {};
    const currentPath = typeof window !== "undefined" ? window.location.pathname : "";
    groups.forEach((g) => {
      const gid = g.group || g.label;
      let stored = null;
      try {
        // Legacy single-key fallback for the operations group.
        if (gid === "operations") stored = localStorage.getItem(OPS_GROUP_KEY);
        if (stored === null) stored = localStorage.getItem(`${OPS_GROUP_KEY}_${gid}`);
      } catch (e) { /* ignore */ }
      if (stored === "0") { state[gid] = false; return; }
      if (stored === "1") { state[gid] = true; return; }
      // No stored preference → collapsed-by-default. Auto-expand only if the
      // current path lives inside this group.
      const hasCurrent = (g.children || []).some((c) => c.to === currentPath);
      state[gid] = hasCurrent;
    });
    return state;
  });
  const toggleGroup = (g) => setGroupsOpen((s) => {
    const next = { ...s, [g]: !s[g] };
    try { localStorage.setItem(`${OPS_GROUP_KEY}_${g}`, next[g] ? "1" : "0"); } catch { /* ignore */ }
    if (g === "operations") {
      try { localStorage.setItem(OPS_GROUP_KEY, next[g] ? "1" : "0"); } catch { /* ignore */ }
    }
    return next;
  });
  const opsOpen = groupsOpen["operations"];
  // Kept as a thin alias for backward compat within this file — always routes
  // through toggleGroup so the per-group localStorage key stays in sync.
  const setOpsOpen = (v) => {
    const next = typeof v === "function" ? v(groupsOpen["operations"]) : v;
    if (next === groupsOpen["operations"]) return;
    toggleGroup("operations");
  };
  const [agencyName, setAgencyName] = useState("");
  const navRef = useRef(null);

  // Operations group child order (per-user, persisted). Resets to defaults
  // when the stored order doesn't match the current available children.
  const opsGroup = SIDEBAR_LINKS_RAW.find((l) => l.group === "operations");
  const defaultOpsOrder = (opsGroup?.children || []).map((c) => c.to);
  const [opsOrder, setOpsOrder] = useState(() => {
    try {
      const raw = localStorage.getItem(OPS_ORDER_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr) && arr.length === defaultOpsOrder.length &&
            defaultOpsOrder.every((p) => arr.includes(p))) return arr;
      }
    } catch { /* ignore */ }
    return defaultOpsOrder;
  });
  const [dragIdx, setDragIdx] = useState(null);
  const dragIdxRef = useRef(null);
  const [overIdx, setOverIdx] = useState(null);
  useEffect(() => {
    try { localStorage.setItem(OPS_ORDER_KEY, JSON.stringify(opsOrder)); } catch { /* ignore */ }
  }, [opsOrder]);
  const reorderOps = (from, to) => {
    if (from === to || from == null || to == null) return;
    setOpsOrder((cur) => {
      const next = cur.slice();
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  // Top-level link order (per-user, persisted). Stored as an array of
  // `topLinkKey` strings. Unknown keys (new links added later) fall back to
  // their natural position via the merge logic at render time.
  const [linksOrder, setLinksOrder] = useState(() => {
    try {
      const raw = localStorage.getItem(LINKS_ORDER_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) return arr;
      }
    } catch { /* ignore */ }
    return [];
  });
  useEffect(() => {
    try { localStorage.setItem(LINKS_ORDER_KEY, JSON.stringify(linksOrder)); } catch { /* ignore */ }
  }, [linksOrder]);
  const [dragTopIdx, setDragTopIdx] = useState(null);
  const dragTopIdxRef = useRef(null);
  const [overTopIdx, setOverTopIdx] = useState(null);

  // Sidebar layout mode (segmented selector at the bottom). When the user
  // drags to reorder, we auto-flip them to "saved" so their change is visible.
  const [layoutMode, setLayoutMode] = useState(() => {
    try {
      const v = localStorage.getItem(LAYOUT_MODE_KEY);
      if (v && LAYOUT_MODES.some((m) => m.id === v)) return v;
    } catch { /* ignore */ }
    return "saved";
  });
  useEffect(() => {
    try { localStorage.setItem(LAYOUT_MODE_KEY, layoutMode); } catch { /* ignore */ }
  }, [layoutMode]);

  // Hydrate from server preferences (once per session, plus on demand via the
  // `irisguard:prefs-updated` window event — fired e.g. when the Users page
  // copies another user's layout onto the current user). Server is source of
  // truth so a dispatcher's layout follows them across workstations. We still
  // mirror everything into localStorage as a cache for instant first paint.
  const prefsHydratedRef = useRef(false);
  const hydratePrefs = useCallback(() => {
    api.get("/users/me/preferences").then((res) => {
      const p = res.data || {};
      if (typeof p.sidebar_layout_mode === "string" &&
          LAYOUT_MODES.some((m) => m.id === p.sidebar_layout_mode)) {
        setLayoutMode(p.sidebar_layout_mode);
      }
      if (Array.isArray(p.sidebar_order)) setLinksOrder(p.sidebar_order);
      if (Array.isArray(p.sidebar_ops_order) &&
          p.sidebar_ops_order.length === defaultOpsOrder.length &&
          defaultOpsOrder.every((x) => p.sidebar_ops_order.includes(x))) {
        setOpsOrder(p.sidebar_ops_order);
      }
    }).catch(() => { /* offline / 401 — fall back to localStorage cache */ });
  }, [defaultOpsOrder]);
  useEffect(() => {
    if (!user || prefsHydratedRef.current) return undefined;
    prefsHydratedRef.current = true;
    hydratePrefs();
    const onUpdate = () => hydratePrefs();
    window.addEventListener("irisguard:prefs-updated", onUpdate);
    return () => window.removeEventListener("irisguard:prefs-updated", onUpdate);
  }, [user, hydratePrefs]);

  // Push prefs changes back to the server (debounced) once hydrated.
  useEffect(() => {
    if (!user || !prefsHydratedRef.current) return undefined;
    const handle = setTimeout(() => {
      api.patch("/users/me/preferences", {
        sidebar_layout_mode: layoutMode,
        sidebar_order: linksOrder,
        sidebar_ops_order: opsOrder,
      }).catch(() => { /* best-effort — localStorage cache still has it */ });
    }, 400);
    return () => clearTimeout(handle);
  }, [user, layoutMode, linksOrder, opsOrder]);
  const reorderTop = (from, to, currentKeys) => {
    if (from === to || from == null || to == null) return;
    const next = currentKeys.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setLinksOrder(next);
  };

  useEffect(() => {
    if (!user) return;
    api.get("/agencies/me")
      .then((r) => setAgencyName(r.data?.name || ""))
      .catch(() => setAgencyName(""));
  }, [user]);

  useEffect(() => {
    try { localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0"); } catch { /* ignore */ }
  }, [collapsed]);
  // NOTE: opsOpen persistence is handled inline by toggleGroup / setOpsOpen so
  // clicking one group doesn't rewrite every group's key on each render.

  const widthClass = collapsed ? "w-16" : "w-60";
  // iter285 — Mobile shell state. On < md screens the sidebar switches
  // from an inline column to an off-canvas drawer that slides in over
  // the content. A dedicated top bar exposes the hamburger + brand.
  // Auto-closes on route change so a nav tap doesn't leave the drawer
  // covering the fresh page.
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <div className="min-h-screen flex bg-[#0A0A0A] text-white">
      {/* Mobile top bar — only visible below md. Fixed so it stays put
          during page scroll; content gets a top-padding compensation
          via `pt-14 md:pt-0` on the main <div> below. */}
      <div
        data-testid="mobile-topbar"
        className="md:hidden fixed top-0 inset-x-0 h-14 z-40 flex items-center gap-3 px-4 bg-[#0A0A0A]/95 backdrop-blur border-b border-[#27272A]"
      >
        <button
          data-testid="mobile-hamburger"
          onClick={() => setMobileOpen(true)}
          aria-label="Open menu"
          className="p-2 -ml-2 text-white hover:text-[#FF3B30] transition-colors"
        >
          <Menu className="size-5" />
        </button>
        <Link to="/" className="flex items-center gap-2 flex-1 min-w-0">
          <BrandLogo variant="icon" height={28} />
          <span className="font-display font-black text-sm uppercase tracking-tighter truncate">
            {agencyName || "Iris Security"}
          </span>
        </Link>
      </div>

      {/* Mobile backdrop — tap-to-close */}
      {mobileOpen && (
        <button
          data-testid="mobile-backdrop"
          onClick={() => setMobileOpen(false)}
          aria-label="Close menu"
          className="md:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
        />
      )}

      <aside
        data-testid="sidebar"
        data-collapsed={collapsed ? "true" : "false"}
        className={`${widthClass} shrink-0 border-r border-[#27272A] flex flex-col bg-[#0A0A0A] sticky top-0 h-screen transition-[width,transform] duration-200
          max-md:fixed max-md:inset-y-0 max-md:left-0 max-md:z-50 max-md:w-72 max-md:!shadow-[10px_0_40px_-10px_rgba(0,0,0,0.9)]
          ${mobileOpen ? "max-md:translate-x-0" : "max-md:-translate-x-full"}`}
      >
        {/* iter280 — Logo + wordmark are now a clickable link to the
            role-appropriate landing page. `/` routes to Overview for
            operational roles; the `<Protected>` component redirects
            client-tier users to `/portal` and officers to `/guard-portal`
            automatically, so a single anchor covers all roles. */}
        <Link
          to="/"
          data-testid="sidebar-brand-home-link"
          aria-label="Return to home"
          className={`py-6 border-b border-[#27272A] flex items-center gap-3 group focus:outline-none focus:bg-[#141414] hover:bg-[#141414] transition-colors ${collapsed ? "px-3 justify-center" : "px-5"}`}
        >
          {collapsed ? (
            <BrandLogo variant="icon" height={32} className="group-hover:opacity-80 transition-opacity" />
          ) : (
            <>
              <BrandLogo variant="icon" height={36} className="group-hover:opacity-80 transition-opacity" />
              <div className="min-w-0">
                <div className="font-display font-black text-lg uppercase leading-none tracking-tighter group-hover:text-[#FF3B30] transition-colors">Iris Security</div>
                <div className="label-mute mt-1" data-testid="agency-name-pill">
                  {agencyName ? agencyName : "CRM • v1"}
                </div>
              </div>
            </>
          )}
        </Link>

        <div className={`flex ${collapsed ? "justify-center" : "justify-end"} px-2 pt-2`}>
          <button
            data-testid="sidebar-toggle"
            onClick={() => setCollapsed((v) => !v)}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="p-1.5 text-zinc-500 hover:text-[#FF3B30] hover:bg-[#121212] transition-colors"
          >
            {collapsed ? <PanelLeftOpen className="size-4" /> : <PanelLeftClose className="size-4" />}
          </button>
        </div>

        {user && (
          <div className={`px-2 mt-2 ${collapsed ? "flex justify-center" : ""}`}>
            <NotificationsBell collapsed={collapsed} />
          </div>
        )}

        <div className="relative flex-1 min-h-0 flex flex-col">
          <nav
            ref={navRef}
            className="flex-1 py-2 overflow-y-auto min-h-0 sidebar-scroll"
            data-testid="sidebar-nav"
          >
          {(() => {
            // iter184e — when a role preview is active, gate the sidebar by
            // the preview role instead of the real user's role. Legacy
            // `adminOnly` / `superOnly` flags read from the same effective
            // role so admins can accurately see a supervisor's / guard's
            // view without logging out.
            const roleForGating = previewRole ? effectiveRole : user?.role;
            const permsForGating = previewRole ? effectivePermissions : permissions;
            const isSupAdmin = roleForGating === "admin" || roleForGating === "supervisor" || roleForGating === "super_admin";
          const CLIENT_TIER = ["client", "client_admin", "regional_manager", "site_manager"];
          const isClientTier = CLIENT_TIER.includes(roleForGating);
          const isGuard = roleForGating === "guard";
          const roleFilter = (l) =>
            (!l.adminOnly || roleForGating === "admin" || roleForGating === "super_admin") &&
            (!l.adminScope || ["admin", "super_admin", "chief_security_officer", "operations_manager"].includes(roleForGating)) &&
            (!l.superOnly || roleForGating === "super_admin") &&
            (!l.supAdminOnly || isSupAdmin) &&
            (isClientTier ? !l.hideForClient : !l.clientOnly) &&
            // Officers see a very narrow slice — only items opting in with
            // `guardOK: true`. Every other sidebar entry is hidden for the
            // guard role so navigating away from /guard-portal doesn't
            // dump them into pages they can't actually use.
            (!isGuard || l.guardOK) &&
            // iter184 — permission-based gating layered on top of the legacy
            // role checks. If a nav item declares a `permission` slug, hide
            // it when the caller lacks that permission. Super-admin bypass
            // stays via the legacy `superOnly` path. Fail-open while
            // permissions haven't loaded yet (permissions=null) so we don't
            // temporarily hide the whole sidebar during first hydrate.
            // During preview, super-admin bypass is intentionally skipped so
            // impersonating "guard" hides admin-only entries even when the
            // real caller is a super-admin.
            (!l.permission
              || (!previewRole && roleForGating === "super_admin")
              || permsForGating == null
              || (Array.isArray(permsForGating) && permsForGating.includes(l.permission)));
          // Prune children that fail the same role filter (e.g. supervisor-visits inside Operations).
          // Applied after label-override mapping so the sidebar renders with
          // both the agency's renamed labels AND the role-appropriate items.
          const links2 = links.map((l) =>
            l.children ? { ...l, children: l.children.filter(roleFilter) } : l
          );
          // iter277 — Drop group headers whose children were all pruned by
          // the permission filter. Otherwise a role stripped of every
          // Operations sub-item would still see an empty "Operations" tab
          // hanging in the sidebar, which reads as "you're allowed but
          // there's nothing here" — misleading and confusing.
          const filtered = links2.filter(
            (l) => roleFilter(l) && !(Array.isArray(l.children) && l.children.length === 0),
          );
            const filteredKeys = filtered.map(topLinkKey);
            let finalKeys;
            if (layoutMode === "default") {
              finalKeys = filteredKeys;
            } else if (layoutMode === "ops-first") {
              const opsKey = "group:operations";
              finalKeys = filteredKeys.includes(opsKey)
                ? [opsKey, ...filteredKeys.filter((k) => k !== opsKey)]
                : filteredKeys;
            } else {
              // "saved" — apply persisted linksOrder, appending unknown items at the end
              const known = linksOrder.filter((k) => filteredKeys.includes(k));
              const unknown = filteredKeys.filter((k) => !known.includes(k));
              finalKeys = [...known, ...unknown];
            }
            const ordered = finalKeys.map((k) => filtered.find((l) => topLinkKey(l) === k));
            const dragLocked = collapsed;
            const dragProps = (idx, key) => dragLocked ? {} : ({
              draggable: true,
              onDragStart: (e) => {
                dragTopIdxRef.current = idx;
                setDragTopIdx(idx);
                // Promote any non-saved mode → "saved" so the drag is durable;
                // capture the currently visible order as the new baseline.
                if (layoutMode !== "saved") {
                  setLinksOrder(finalKeys);
                  setLayoutMode("saved");
                }
                try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", String(key)); } catch { /* ignore */ }
              },
              onDragOver: (e) => {
                e.preventDefault();
                if (overTopIdx !== idx) setOverTopIdx(idx);
                try { e.dataTransfer.dropEffect = "move"; } catch { /* ignore */ }
              },
              onDragLeave: () => { if (overTopIdx === idx) setOverTopIdx(null); },
              onDrop: (e) => {
                e.preventDefault();
                reorderTop(dragTopIdxRef.current, idx, finalKeys);
                dragTopIdxRef.current = null;
                setDragTopIdx(null); setOverTopIdx(null);
              },
              onDragEnd: () => { dragTopIdxRef.current = null; setDragTopIdx(null); setOverTopIdx(null); },
            });
            const wrapCls = (idx) =>
              overTopIdx === idx && dragTopIdx !== null && dragTopIdx !== idx
                ? "ring-1 ring-inset ring-[#FF3B30]"
                : dragTopIdx === idx ? "opacity-50" : "";
            return ordered.map((l, idx) => {
              if (l.children) {
                // Collapsible group header — Operations, Client/Sites, Officers
                const Icon = l.icon;
                const gid = l.group || l.label;
                const isOps = gid === "operations";
                const isOpen = groupsOpen[gid];
                const anyActive = typeof window !== "undefined" &&
                  l.children.some((c) => window.location.pathname === c.to);
                // Per-group child order — Operations honours the user's saved order;
                // other groups render in declaration order (no drag reorder yet).
                const childOrder = isOps
                  ? opsOrder.map((path) => l.children.find((c) => c.to === path)).filter(Boolean)
                  : l.children;
                if (collapsed) {
                  // When sidebar collapsed, render child icons inline
                  return childOrder.map((c) => {
                    const badgeCount = c.badgeKey ? (badgeCounts[c.badgeKey] || 0) : 0;
                    return (
                      <NavLink
                        key={c.to}
                        to={c.to}
                        data-testid={`nav-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                        title={badgeCount > 0 ? `${c.label} (${badgeCount})` : c.label}
                        className={({ isActive }) =>
                          `relative flex items-center justify-center px-0 py-3 text-sm tracking-wide transition-colors duration-150 border-l-2 ${
                            isActive ? "bg-[#121212] border-[#FF3B30] text-white"
                                     : "border-transparent text-zinc-400 hover:bg-[#121212] hover:text-white"
                          }`
                        }
                      >
                        <c.icon className="size-4 shrink-0" strokeWidth={2} />
                        {badgeCount > 0 && (
                          <span
                            data-testid={`nav-badge-${c.badgeKey}-collapsed`}
                            className="absolute top-1 right-1 min-w-[16px] h-4 px-1 text-[10px] font-mono font-bold bg-[#FF3B30] text-white rounded-full flex items-center justify-center leading-none"
                          >
                            {badgeCount > 99 ? "99+" : badgeCount}
                          </span>
                        )}
                      </NavLink>
                    );
                  });
                }
                return (
                  <div
                    key={l.label}
                    data-testid={`nav-group-${gid}`}
                    {...dragProps(idx, topLinkKey(l))}
                    className={wrapCls(idx)}
                  >
                    <div
                      className={`w-full flex items-stretch border-l-2 ${
                        anyActive
                          ? "border-[#FF3B30]"
                          : "border-transparent"
                      }`}
                    >
                      <button
                        type="button"
                        data-testid={`nav-${gid}-toggle`}
                        onClick={() => (isOps ? setOpsOpen((v) => !v) : toggleGroup(gid))}
                        className={`flex-1 flex items-center gap-3 px-5 py-3 text-sm tracking-wide transition-colors duration-150 ${
                          anyActive
                            ? "text-white"
                            : "text-zinc-400 hover:bg-[#121212] hover:text-white"
                        }`}
                      >
                        <Icon className="size-4 shrink-0" strokeWidth={2} />
                        <span className="uppercase font-medium flex-1 text-left">{l.label}</span>
                        {isOpen ? <ChevronDown className="size-3.5 text-zinc-500" /> : <ChevronRight className="size-3.5 text-zinc-500" />}
                      </button>
                      {!collapsed && isOps && (
                        <button
                          type="button"
                          data-testid="nav-operations-popout"
                          title="Click: pop out Roster, Tardies, Incidents & Daily Activity into separate windows · Shift+click: reset saved window layout"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (e.shiftKey) {
                              const n = clearAllGeometry();
                              try {
                                localStorage.removeItem(OPS_ORDER_KEY);
                                localStorage.removeItem(LINKS_ORDER_KEY);
                                localStorage.removeItem(LAYOUT_MODE_KEY);
                              } catch { /* ignore */ }
                              setOpsOrder(defaultOpsOrder);
                              setLinksOrder([]);
                              setLayoutMode("default");
                              api.patch("/users/me/preferences", {
                                sidebar_layout_mode: "default",
                                sidebar_order: [],
                                sidebar_ops_order: defaultOpsOrder,
                              }).catch(() => { /* ignore */ });
                              toast.success(n ? `Window layout reset (${n} saved) · sidebar restored` : "Sidebar & layout reset");
                              return;
                            }
                            const blocked = popOutOpsWindows();
                            if (blocked.length) {
                              toast.error(`Browser blocked ${blocked.length} popup${blocked.length === 1 ? "" : "s"}. Allow popups for this site.`);
                            } else {
                              toast.success("Operations multi-window opened");
                            }
                          }}
                          className="px-3 text-zinc-500 hover:text-[#FF3B30] hover:bg-[#121212] border-l border-[#27272A]"
                        >
                          <PictureInPicture2 className="size-3.5" strokeWidth={2} />
                        </button>
                      )}
                    </div>
                    {isOpen && (
                      <div className="border-l-2 border-[#27272A] ml-5" data-testid={`nav-${gid}-children`}>
                        {childOrder.map((c, cidx) => {
                          // Only operations has drag reorder; other groups render statically.
                          const dragHandlers = isOps ? {
                            draggable: true,
                            onDragStart: (e) => {
                              dragIdxRef.current = cidx;
                              setDragIdx(cidx);
                              try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", c.to); } catch { /* ignore */ }
                            },
                            onDragEnter: () => setOverIdx(cidx),
                            onDragOver: (e) => e.preventDefault(),
                            onDrop: (e) => {
                              e.preventDefault();
                              const from = dragIdxRef.current;
                              reorderOps(from, cidx);
                              dragIdxRef.current = null;
                              setDragIdx(null); setOverIdx(null);
                            },
                            onDragEnd: () => { dragIdxRef.current = null; setDragIdx(null); setOverIdx(null); },
                          } : {};
                          const isDragging = isOps && dragIdx === cidx;
                          const isOver = isOps && overIdx === cidx && dragIdx !== cidx;
                          return (
                            <NavLink
                              key={c.to}
                              to={c.to}
                              {...dragHandlers}
                              data-testid={`nav-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                              className={({ isActive }) =>
                                `flex items-center gap-3 pl-4 pr-5 py-2 text-sm tracking-wide transition-colors duration-150 ${isDragging ? "opacity-40" : ""} ${isOver ? "bg-[#FF3B30]/10" : ""} ${
                                  isActive ? "bg-[#121212] text-white"
                                           : "text-zinc-400 hover:bg-[#121212] hover:text-white"
                                }`
                              }
                            >
                              <c.icon className="size-3.5 shrink-0" strokeWidth={2} />
                              <span className="flex-1">{c.label}</span>
                              {c.badgeKey && (badgeCounts[c.badgeKey] || 0) > 0 && (
                                <span
                                  data-testid={`nav-badge-${c.badgeKey}`}
                                  className="min-w-[20px] px-1.5 py-0.5 text-[10px] font-mono font-bold bg-[#FF3B30] text-white rounded-full flex items-center justify-center leading-none"
                                >
                                  {badgeCounts[c.badgeKey] > 99 ? "99+" : badgeCounts[c.badgeKey]}
                                </span>
                              )}
                            </NavLink>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              const Icon = l.icon;
              return (
                <div
                  key={l.to}
                  {...dragProps(idx, topLinkKey(l))}
                  className={wrapCls(idx)}
                >
                  <NavLink
                    to={l.to}
                    end={l.end}
                    data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    title={collapsed ? l.label : undefined}
                    className={({ isActive }) =>
                      `flex items-center ${collapsed ? "justify-center px-0" : "gap-3 px-5"} py-3 text-sm tracking-wide transition-colors duration-150 border-l-2 ${
                        isActive
                          ? "bg-[#121212] border-[#FF3B30] text-white"
                          : "border-transparent text-zinc-400 hover:bg-[#121212] hover:text-white"
                      }`
                    }
                  >
                    <Icon className="size-4 shrink-0" strokeWidth={2} />
                    {!collapsed && <span className="uppercase font-medium">{l.label}</span>}
                  </NavLink>
                </div>
              );
            });
          })()}
          </nav>
          <SidebarBackToTop targetRef={navRef} collapsed={collapsed} />
        </div>
      </aside>

      <main className="flex-1 min-w-0 pt-14 md:pt-0">
        <RolePreviewBanner />
        <UserMenu
          extra={(
            <div data-testid="sidebar-layout-switch">
              <div className="label-mute mb-1.5">Sidebar layout</div>
              <div className="grid grid-cols-3 border border-[#27272A] divide-x divide-[#27272A]">
                {LAYOUT_MODES.map((m) => {
                  const active = layoutMode === m.id;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      data-testid={`sidebar-mode-${m.id}`}
                      title={m.hint}
                      onClick={() => setLayoutMode(m.id)}
                      className={`px-1 py-1.5 text-[10px] uppercase tracking-widest transition-colors duration-150 ${
                        active
                          ? "bg-[#FF3B30] text-white"
                          : "text-zinc-500 hover:text-white hover:bg-[#121212]"
                      }`}
                    >
                      {m.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        />
        {children}
      </main>
      <BackToTop />
      {(mustChangePw) && (
        <ChangePasswordModal
          forced={mustChangePw}
        />
      )}
    </div>
  );
}
